// Virtual Buyer API types - mirror the Django `buyer` app serializers.
// Outward casing follows the backend (snake_case on buyer/question rows,
// camelCase on the analysis shadow, per AnalysisShadowSerializer).

import type { Big5Category } from "@/components/vb/Big5Chip";
import type { VbStatus } from "@/components/vb/StatusPill";

export type { Big5Category, VbStatus };

/** AITS scores businesses on 19 AI Trust Signals - the headline denominator for
 *  "signals exposed (X/19)". (The buyer judge evaluates 18 leaf signals; the
 *  count is displayed against the 19-signal product headline. See the backend
 *  `buyer/services/metrics.py` TRUST_SIGNALS_TOTAL.) */
export const TRUST_SIGNALS_TOTAL = 19;

/** DRF default pagination envelope (list endpoints return this, not a bare array). */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** Global, URL-deduplicated company pointer returned on a Buyer. */
export interface VbCompanyRef {
  id: number;
  url: string;
  name: string;
}

/** Per-buyer competitor (BuyerCompetitorSerializer). */
export interface BuyerCompetitor {
  id: number;
  name: string;
  url: string;
  created_ts: string;
  updated_ts: string;
}

/** Provenance of a question row. */
export type QuestionSource = "GENERATED" | "EDITED" | "MANUAL";

/** Big-5 question (QuestionSerializer). */
export interface Question {
  id: string; // UUID
  buyer: number;
  category: Big5Category;
  text: string;
  source: QuestionSource;
  original_text: string | null;
  generation_run_id: string | null;
  created_ts: string;
  updated_ts: string;
}

/** Newest analysis of any status for a buyer - drives the overview status pill. */
export interface BuyerLatestAnalysis {
  id: number;
  status: VbStatus;
  completed_at: string | null;
}

/** VB-METRICS off the buyer's latest DONE analysis (null until one completes).
 *  `visibility` / `sov` are fractions in [0, 1]. `visibility_delta` is the
 *  run-over-run change vs the previous DONE analysis (null with <2 runs).
 *  `visibility_history` is the last ≤6 DONE visibilities, oldest→newest. */
export interface BuyerMetrics {
  signals_exposed: number | null;
  visibility: number | null;
  sov: number | null;
  visibility_delta: number | null;
  /** Signals-exposed change vs the previous DONE run (null with <2 runs). */
  signals_exposed_delta: number | null;
  visibility_history: number[];
}

/** Buyer (BuyerSerializer). `company_url` is write-only and not present on reads.
 *  `latest_analysis` / `metrics` are populated on the list AND detail endpoints
 *  (both prefetch recent analyses); null on create responses. */
export interface Buyer {
  id: number;
  name: string;
  description: string;
  language: string;
  market_region: string;
  market_city: string;
  company: VbCompanyRef;
  competitors: BuyerCompetitor[];
  suggested_snapshot: Record<string, unknown> | null;
  edited_fields: string[];
  latest_analysis: BuyerLatestAnalysis | null;
  metrics: BuyerMetrics | null;
  created_ts: string;
  updated_ts: string;
}

/** Thin Django analysis shadow (AnalysisShadowSerializer). The metrics are
 *  denormalized off the run and null on non-DONE rows (and DONE rows whose
 *  judgments all failed); `visibility` / `sov` are fractions in [0, 1]. */
export interface AnalysisShadow {
  id: number;
  status: VbStatus;
  buyer: { id: number; name: string };
  jartsAnalysisId: string;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdTs: string;
  signalsExposedCount: number | null;
  visibility: number | null;
  sov: number | null;
}

/** Composed analysis detail (AnalysisDetailSerializer). `progress` and `result`
 *  are Jarts-owned passthrough blobs and are mutually exclusive across the
 *  lifecycle - discriminate on `status`. */
export interface AnalysisDetail extends AnalysisShadow {
  progress: Record<string, unknown> | null;
  result: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Jarts analysis result envelope (the `result` blob on AnalysisDetail when a
// run is terminal). Mirrors `Analysis.to_dict()` in jarts/app/models.py. These
// are defensive read shapes - every field is optional because the blob is
// Jarts-owned and we never want a missing key to throw. The verdict screen
// (VB-F1/F2) reads these via `verdictModel.ts`.
// ---------------------------------------------------------------------------

/** One AI Trust Signal verdict for one named company in one answer. `name` is
 *  carried inline by the judge, so the frontend needs no key→name map to label
 *  surfaced signals. `key` matches reports.SignalDefinition.key (1, 3-16, 18-20). */
export interface ResultSignal {
  key?: number;
  name?: string;
  exposed?: boolean;
  evidence?: string;
  judgedBy?: string[];
}

/** A company the judge enumerated in an answer. `isFocus` marks the buyer's
 *  target company; everyone else is a competitor / third party. */
export interface ResultSubject {
  company?: string;
  isFocus?: boolean;
  signals?: ResultSignal[];
}

export interface ResultJudgment {
  focusCompany?: string;
  subjects?: ResultSubject[];
}

/** One cited source on a single (run, question). `sourceType` is the Jarts
 *  orbit relative to the focus company (You / Peer / 3rd / Other); `category`
 *  is the URL-classifier bucket; `competitorBrand` is server-resolved against
 *  the buyer's competitor roster (null when not a competitor). */
export interface ResultCitation {
  url?: string;
  displayUrl?: string;
  host?: string;
  rootDomain?: string;
  category?: string;
  sourceType?: string;
  isCompetitorDomain?: boolean;
  competitorBrand?: string | null;
  model?: string;
}

/** One (run, question) row: the verbatim answer + per-question detail. */
export interface ResultRunQuestion {
  questionId?: string;
  position?: number;
  answerText?: string | null;
  mentions?: Record<string, number> | null;
  citations?: ResultCitation[] | null;
  judgment?: ResultJudgment | null;
}

/** One provider run (OpenAI / Gemini / Perplexity) within an analysis. */
export interface ResultRun {
  key?: string;
  provider?: string;
  model?: string;
  status?: string;
  questions?: ResultRunQuestion[];
}

/** Analysis-level brand roster entry. `isCustomer` is the focus flag. */
export interface ResultBrand {
  name?: string;
  canonicalName?: string;
  domain?: string;
  isCustomer?: boolean;
  mentionCount?: number;
}

/** The terminal `result` blob, typed for the verdict screen. */
export interface AnalysisResultEnvelope {
  questions?: { id: string; text: string; category: Big5Category }[];
  competitors?: { name?: string; url?: string; domain?: string }[];
  brands?: ResultBrand[];
  runs?: ResultRun[];
  /** Per-question narrative summary keyed by Question UUID (analyses.question_commentary).
   *  Null / missing for runs predating the commentary pass - the UI then falls
   *  back to a deterministic sentence. */
  questionCommentary?: Record<string, string> | null;
  company?: Record<string, unknown>;
  persona?: Record<string, unknown>;
}

// Virtual Buyer run-completed analytics (the `RunCompletionMetrics` payload +
// `MonthlyStrength`) used to live here. The run terminals moved server-side
// (buyer/services/run_events.py derives the metrics from the same result
// envelope), so there's no client-side payload type to share anymore (AITS-545).

// ---------------------------------------------------------------------------
// Buyer-creation wizard (VB-B1 + VB-B2) write contracts.
// ---------------------------------------------------------------------------

/** Verbatim response of POST /api/buyers/suggest/ (BuyerSuggestResponseSerializer).
 *  Captured as-is into `suggested_snapshot` on create so the backend can compute
 *  Suggested→Edited diffs. `market_region` is an ISO-3166 alpha-2 country code.
 *  `competitors` are LLM-suggested ({name, url}, ≤5) - best-effort, may be []. */
export interface SuggestResponse {
  name: string;
  description: string;
  language: string;
  market_region: string;
  market_city: string;
  competitors?: CompetitorInput[];
}

/** A competitor as written on create / via the competitor endpoints. */
export interface CompetitorInput {
  name: string;
  url: string;
}

/** Optional in-progress buyer the wizard sends to POST /api/buyers/suggest/ so
 *  Marcus fills only the empty fields and biases competitors toward the persona
 *  the user is actually building. All fields optional. */
export interface SuggestDraft {
  name?: string;
  description?: string;
  market_region?: string;
  market_city?: string;
}

/** POST /api/buyers/ body. The company is identified by id (the wizard enters
 *  scoped to a pre-selected `?company=<id>`), never a URL. */
export interface CreateBuyerPayload {
  company_id: number;
  name: string;
  description: string;
  language: string;
  market_region: string;
  market_city: string;
  suggested_snapshot: SuggestResponse | null;
  competitors: CompetitorInput[];
}

/** Stable public error codes the buyer/question/competitor endpoints return in
 *  the `code` field of a 4xx body. Used to map to inline UI messages. */
export type VbErrorCode =
  | "CONTEXT_MISSING"
  | "DUPLICATE_NAME"
  | "DUPLICATE_URL"
  | "COMPANY_BUYER_CAP_REACHED"
  | "TOTAL_CAP_REACHED"
  | "CATEGORY_CAP_REACHED"
  | "NO_QUESTIONS"
  | "INVALID_URL"
  | "CANNOT_INFER"
  | "THROTTLED"
  | "ANALYSIS_IN_PROGRESS";

// ---------------------------------------------------------------------------
// Reputation Graph Scoring (RGS) — per-report Sources read API (RGS-3).
// Shape of GET /api/reports/<id>/sources/. Field names mirror the backend
// (reports/graph/sources.py) verbatim. The reputation readout card (RGS-4)
// consumes only `summary`; the full response is typed so the Sources page
// (RGS-5) reuses it.
// ---------------------------------------------------------------------------

/** Citation orbit relative to the scored company. The control gradient: you own
 *  it ("owned") → you can sway it ("influenced") → strangers ("earned"). */
export type Orbit = "owned" | "influenced" | "earned";

/** Per-orbit rollup. `percentage` is by citation count and the three orbits sum
 *  to 100 (largest-remainder rounded). `top_domains` is ≤3, most-cited first. */
export interface OrbitSummary {
  orbit: Orbit;
  citations: number;
  percentage: number;
  sources_count: number;
  top_domains: string[];
}

/** Headline rollup for one report (one calendar-month snapshot). */
export interface SourcesSummary {
  period: string; // e.g. "February 2026" — the month label
  period_month: string; // "YYYY-MM"
  sources_count: number;
  citations_count: number; // 0 ⇒ the card hides
  orbits: OrbitSummary[];
}

/** One distinct cited page (used by the Sources page table — RGS-5). */
export interface SourceRow {
  id: string;
  domain: string;
  display_url: string;
  path: string;
  orbit: Orbit | null;
  kind: string | null;
  citations: number;
  signals: string[];
  models: string[];
  last_seen: string; // ISO-8601
  series: number[]; // 6-point monthly trend
}

/** GET /api/reports/<id>/sources/ response. */
export interface ReportSourcesResponse {
  summary: SourcesSummary;
  sources: SourceRow[];
}

// --- Per-source detail (RGS-5 drawer) — GET /reports/<id>/sources/<source_id>/ ---

/** A signal this page was cited for, with its current health on the report.
 *  `status` drives the card's left-bar colour + badge. */
export interface SourceSignal {
  name: string; // SignalDefinition.name (the formal AI Trust Signal name)
  category: string; // "technical" | "authority" | "brand"
  status: "healthy" | "issue" | "critical";
  score: string; // raw Signal.score: "pass" | "high" | "medium" | "fail" | "low"
}

/** One month of this page's citation history (6 points, chronological). */
export interface SourceHistoryPoint {
  period_month: string; // "YYYY-MM"
  citations: number;
}

/** GET /reports/<id>/sources/<source_id>/ — drawer drill-down. The backend also
 *  returns `snippets[]` (the verbatim model text), but RGS-5 does not render it
 *  (DeepDive call, Jun 12 — we can't honestly attribute a span to a source). */
export interface SourceDetail {
  id: string;
  domain: string;
  display_url: string;
  path: string;
  orbit: Orbit | null;
  kind: string | null;
  title: string;
  citations: number;
  last_seen: string; // ISO-8601
  signals: SourceSignal[];
  models: string[];
  history: SourceHistoryPoint[];
  unhealthy_signals_count: number;
}
